// @ts-nocheck
import {
  FETCH_CONTRACTOR_RATING_REQUEST,
  FETCH_CONTRACTOR_RATING_SUCCESS,
  FETCH_CONTRACTOR_RATING_FAILURE,
  FETCH_CONTRACTOR_RATINGS_BATCH_REQUEST,
  FETCH_CONTRACTOR_RATINGS_BATCH_SUCCESS,
  FETCH_CONTRACTOR_RATINGS_BATCH_FAILURE,
  UPSERT_CONTRACTOR_RATING_REQUEST,
  UPSERT_CONTRACTOR_RATING_SUCCESS,
  UPSERT_CONTRACTOR_RATING_FAILURE,
  ADD_CONTRACTOR_RATING_NOTE_REQUEST,
  ADD_CONTRACTOR_RATING_NOTE_SUCCESS,
  ADD_CONTRACTOR_RATING_NOTE_FAILURE,
  REMOVE_CONTRACTOR_RATING_NOTE_REQUEST,
  REMOVE_CONTRACTOR_RATING_NOTE_SUCCESS,
  REMOVE_CONTRACTOR_RATING_NOTE_FAILURE,
} from "../constants/contractorRatings";
import {
  addContractorRatingNote,
  deleteContractorRatingNote,
  fetchContractorRating,
  fetchContractorRatingsBatch,
  upsertContractorRating,
} from "../../api/contractor-ratings";

/**
 * Fetch the internal rating of a single contractor.
 *
 * Backend returns an empty placeholder when the doc does not exist — it does
 * not treat 404 as an error. Caller must gate by role before dispatching
 * (RolesGuard 403 for contractor/client/freelanceEc).
 */
export const fetchContractorRatingByIdAction =
  ({ contractorId, successCallback, failureCallback }) =>
  async (dispatch) => {
    dispatch({
      type: FETCH_CONTRACTOR_RATING_REQUEST,
      payload: { contractorId },
    });
    const { response, error } = await fetchContractorRating(contractorId);
    if (response?.data) {
      const body = response.data?.data ?? response.data;
      dispatch({
        type: FETCH_CONTRACTOR_RATING_SUCCESS,
        payload: { contractorId, data: body },
      });
      successCallback?.(body);
    } else {
      dispatch({
        type: FETCH_CONTRACTOR_RATING_FAILURE,
        payload: { contractorId, error },
      });
      failureCallback?.(error);
    }
  };

/**
 * Batch fetch for listings (Contractors page). Caller must gate by
 * role and pass only valid ids from the current page.
 */
export const fetchContractorRatingsBatchAction =
  ({ contractorIds, successCallback, failureCallback }) =>
  async (dispatch) => {
    if (!contractorIds?.length) return;
    dispatch({ type: FETCH_CONTRACTOR_RATINGS_BATCH_REQUEST });
    const { response, error } = await fetchContractorRatingsBatch(contractorIds);
    if (response?.data) {
      const body = response.data?.data ?? response.data;
      const items = body?.items ?? body;
      dispatch({
        type: FETCH_CONTRACTOR_RATINGS_BATCH_SUCCESS,
        payload: items,
      });
      successCallback?.(body);
    } else {
      dispatch({
        type: FETCH_CONTRACTOR_RATINGS_BATCH_FAILURE,
        payload: error,
      });
      failureCallback?.(error);
    }
  };

/**
 * Upserts the score and, when `note` has text, appends it as a comment in the
 * same request. The reducer replaces `byContractorId[contractorId]` with the
 * document the server returns.
 */
export const upsertContractorRatingAction =
  ({ contractorId, score, note, successCallback, failureCallback }) =>
  async (dispatch) => {
    dispatch({
      type: UPSERT_CONTRACTOR_RATING_REQUEST,
      payload: { contractorId },
    });
    const { response, error } = await upsertContractorRating(contractorId, {
      score,
      ...(note != null ? { note } : {}),
    });
    if (response?.data) {
      const body = response.data?.data ?? response.data;
      dispatch({
        type: UPSERT_CONTRACTOR_RATING_SUCCESS,
        payload: { contractorId, data: body },
      });
      successCallback?.(body);
    } else {
      dispatch({
        type: UPSERT_CONTRACTOR_RATING_FAILURE,
        payload: { contractorId, error },
      });
      failureCallback?.(error);
    }
  };

/**
 * Adds a comment without touching the score — used by the card, where there is
 * no star input to save alongside it.
 */
export const addContractorRatingNoteAction =
  ({ contractorId, text, successCallback, failureCallback }) =>
  async (dispatch) => {
    dispatch({
      type: ADD_CONTRACTOR_RATING_NOTE_REQUEST,
      payload: { contractorId },
    });
    const { response, error } = await addContractorRatingNote(
      contractorId,
      text,
    );
    if (response?.data) {
      const body = response.data?.data ?? response.data;
      dispatch({
        type: ADD_CONTRACTOR_RATING_NOTE_SUCCESS,
        payload: { contractorId, data: body },
      });
      successCallback?.(body);
    } else {
      dispatch({
        type: ADD_CONTRACTOR_RATING_NOTE_FAILURE,
        payload: { contractorId, error },
      });
      failureCallback?.(error);
    }
  };

/**
 * Removes one comment. The backend allows the author or a superAdmin only, so
 * the UI hides the button for everyone else — a 403 here is a bug, not a flow.
 */
export const removeContractorRatingNoteAction =
  ({ contractorId, noteId, successCallback, failureCallback }) =>
  async (dispatch) => {
    dispatch({
      type: REMOVE_CONTRACTOR_RATING_NOTE_REQUEST,
      payload: { contractorId },
    });
    const { response, error } = await deleteContractorRatingNote(
      contractorId,
      noteId,
    );
    if (response?.data) {
      const body = response.data?.data ?? response.data;
      dispatch({
        type: REMOVE_CONTRACTOR_RATING_NOTE_SUCCESS,
        payload: { contractorId, data: body },
      });
      successCallback?.(body);
    } else {
      dispatch({
        type: REMOVE_CONTRACTOR_RATING_NOTE_FAILURE,
        payload: { contractorId, error },
      });
      failureCallback?.(error);
    }
  };
